import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";

export default function Hero() {
  // Video path - using the actual video file name
  // Note: Spaces in filenames should work, but if issues occur, rename the file without spaces
  const videoPath = "/videos/WhatsApp Video 2026-01-13 at 11.47.57 AM.mp4";
  const videoRef = useRef(null);

  useEffect(() => {
    // Ensure video plays when component mounts
    const playVideo = async () => {
      if (videoRef.current) {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.log("Video autoplay failed:", error);
          // Try to play on user interaction
          const handleUserInteraction = () => {
            if (videoRef.current) {
              videoRef.current.play();
            }
            document.removeEventListener("click", handleUserInteraction);
            document.removeEventListener("touchstart", handleUserInteraction);
          };
          document.addEventListener("click", handleUserInteraction);
          document.addEventListener("touchstart", handleUserInteraction);
        }
      }
    };
    playVideo();
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          onLoadedData={() => {
            console.log("Video loaded successfully");
            if (videoRef.current) {
              videoRef.current.play().catch(console.error);
            }
          }}
          onError={(e) => {
            console.error("Video error:", e);
            console.error("Video path:", videoPath);
            console.error("Make sure the video file exists at:", videoPath);
          }}
        >
          <source src={videoPath} type="video/mp4" />
          {/* Fallback if video doesn't load */}
          Your browser does not support the video tag.
        </video>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 text-center">
        <div className="max-w-4xl mx-auto">
          {/* VREELS Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-center mb-0 -mb-2"
          >
            <img
              src="/images/logonewnobg.png"
              alt="VREELS Logo"
              className="h-40 md:h-48 lg:h-56 xl:h-64 w-auto"
            />
          </motion.div>

          {/* Main Headline - "Reel It Like You Mean It" with gradient */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight -mt-4"
          >
            <span
              className="bg-gradient-to-r from-[#FF8C3B] via-[#FF6365] to-[#FF6365] bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(to right, #FF8C3B, #FF6365)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Reel It Like You Mean It
            </span>
          </motion.div>

          {/* Second Headline - "Create Amazing Reels" in white */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mt-4"
          >
            Create Amazing Reels
          </motion.div>

          {/* Description Paragraph */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-xs md:text-sm text-white max-w-2xl mx-auto leading-relaxed mt-6"
          >
            The ultimate creative platform where your content comes alive. Create
            stunning reels, share memorable moments, build amazing Pix Pouches,
            and discover trending products.
          </motion.p>

          {/* Download Our App Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="space-y-6 mt-8"
          >
            {/* CTA Headline - "Download Our App" with gradient */}
            <div className="text-lg md:text-xl lg:text-2xl xl:text-3xl">
              <span
                className="bg-gradient-to-r from-[#FF8C3B] via-[#FF6365] to-[#FF6365] bg-clip-text text-transparent"
                style={{
                  backgroundImage: "linear-gradient(to right, #FF8C3B, #FF6365)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Download Our App
              </span>
            </div>

            {/* CTA Sub-description */}
            <p className="text-xs md:text-sm text-white max-w-xl mx-auto">
              Get VREELS on your mobile device and start sharing your moments!
            </p>

            {/* Download Section - Clean Unified Design */}
            <div className="flex flex-col items-center justify-center mt-8">
              {/* Unified Download Container */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20 shadow-2xl max-w-2xl w-full"
              >
                {/* QR Code Section */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
                  {/* QR Code - Left Side */}
                  <div className="flex-shrink-0">
                    <div className="bg-white rounded-xl p-3 shadow-lg">
                      <img
                        src="/images/qr.png"
                        alt="QR Code to download VREELS app"
                        className="w-28 h-28 md:w-32 md:h-32"
                      />
                    </div>
                    <p className="text-xs text-white/80 text-center mt-2">Scan QR Code</p>
                  </div>

                  {/* Divider */}
                  <div className="hidden md:block w-px h-20 bg-white/20"></div>
                  <div className="md:hidden w-20 h-px bg-white/20"></div>

                  {/* App Store Buttons - Right Side */}
                  <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
              {/* Apple App Store Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="w-[180px] bg-white rounded-xl overflow-hidden"
              >
                <a
                  href="https://apps.apple.com/us/app/vreels/id6744721098"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-black px-4 py-3 hover:opacity-90 transition-opacity w-full"
                >
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-black"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="text-xs leading-tight text-black">Download on the</span>
                    <span className="text-xs font-semibold leading-tight text-black">
                      Apple Store
                    </span>
                  </div>
                </a>
              </motion.div>

              {/* Google Play Store Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="w-[180px] bg-white rounded-xl overflow-hidden"
              >
                <a
                  href="https://play.google.com/store/apps/details?id=com.mnk.vreels"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-black px-4 py-3 hover:opacity-90 transition-opacity w-full"
                >
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-black"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="text-xs leading-tight text-black">GET IT ON</span>
                    <span className="text-xs font-semibold leading-tight text-black">
                      Google Play
                    </span>
                  </div>
                </a>
              </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
